import { useSyncExternalStore } from "react";

import {
  assinarRegistry,
  getPerfil,
  listarPerfis,
  usuarioAtual,
  type PerfilRegistry,
} from "@/api/registry";

/**
 * Identidade e permissões da sessão.
 *
 * Mantém a mesma superfície do módulo original do MVP — `useRole`, `can`,
 * `canAccess`, `personalPersonId` e os predicados por perfil — para que as
 * onze telas não precisem ser reescritas. O que mudou é a fonte: era
 * `localStorage["soap.role"]`, trocável num `<select>` no topo da tela; agora
 * é o perfil que o servidor devolve para a sessão autenticada.
 *
 * Isto aqui **não é** controle de acesso: serve para esconder o que não faz
 * sentido mostrar. A autorização de verdade é filtro de WHERE no servidor
 * (`EscopoService`) e guard de permissão (`PermissoesGuard`).
 */

/** O perfil é configurável no M10 — por isso o "role" é o nome do perfil. */
export type Role = string;

export type NavKey =
  | "/loja"
  | "/pipeline"
  | "/dashboard"
  | "/notificacoes"
  | "/solicitacoes"
  | "/parametrizacao"
  | "/perfis"
  | "/admin-ia"
  | "fit";

export type PermKey =
  | "verTudo"
  | "editarCard"
  | "moverCard"
  | "criarCard"
  | "alocar"
  | "aprovarSolicitacoes"
  | "registrarProducao"
  | "validarProducao"
  | "editarParametros"
  | "gerenciarUsuarios"
  | "somenteLeitura";

function perfilDaSessao(): PerfilRegistry | undefined {
  const usuario = usuarioAtual();
  return usuario ? getPerfil(usuario.perfilId) : undefined;
}

function roleAtual(): Role {
  return perfilDaSessao()?.nome ?? "";
}

/** Re-renderiza quando o registry é hidratado ou invalidado pelo WebSocket. */
export function useRole(): Role {
  return useSyncExternalStore(assinarRegistry, roleAtual, () => "");
}

export function can(_role: Role, perm: PermKey): boolean {
  return usuarioAtual()?.permissoes.includes(perm) ?? false;
}

export function canAccess(_role: Role, key: NavKey): boolean {
  return perfilDaSessao()?.telas.includes(key) ?? false;
}

export const isReadOnly = (r: Role) => can(r, "somenteLeitura");

// Predicados por nome de perfil. Os perfis são configuráveis, então isto vale
// só para afinar a apresentação — nunca para decidir acesso.
export const isAdmin = (r: Role) => r === "Admin";
export const isGestor = (r: Role) => r === "Gestor";
export const isPlanejamento = (r: Role) => r === "Planejamento";
export const isRoteirista = (r: Role) => r === "Roteirista";
export const isCoordenador = (r: Role) => r === "Coordenador de Projetos";
export const isDesigner = (r: Role) => r === "Diretor de Arte e Designer";
export const isFreelancer = (r: Role) => r === "Freelancer";
export const isComercial = (r: Role) => r === "Comercial";

/**
 * Profissional vinculado ao usuário, ou null quando o perfil enxerga a
 * operação inteira. Antes isto morava no *perfil*, de modo que dois designers
 * no mesmo perfil eram a mesma pessoa.
 */
export function personalPersonId(_role: Role): string | null {
  const usuario = usuarioAtual();
  if (!usuario) return null;
  const perfil = getPerfil(usuario.perfilId);
  if (perfil?.escopoFlow === "COMPLETA" && perfil.escopoPipeline === "TODOS") return null;
  return usuario.profissionalId;
}

export function allRoles(): Role[] {
  return listarPerfis().map((p) => p.nome);
}

/** Mock: projetos com notificação comercial pendente (IV, V1, Aprovado). */
export const PENDING_COMMERCIAL_NOTIF = new Set(["pr1", "pr5", "pr9", "pr10"]);
